import { useContext } from "react";

import classNames from "classnames";
import Spinner from "~/common/components/Spinner/Spinner";
import PackageEntryList from "~/features/cart/activeQuote/components/productTable/PackageEntryList";
import { ActiveQuoteShowInternalContext } from "~/features/cart/activeQuote/components/ActiveQuoteEdit/ActiveQuoteEdit";
import { IconType } from "~/common/components/icon/IconType";
import CartEntryProductInfo from "~/features/cart/cart/components/CartEntryProductInfo";
import { isConfiguratorProduct } from "~/domains/product/types/ProductData";
import {
  DeliverySubtotal,
  PriceSubtotal,
  QuantitySubtotal,
  TargetPriceSubtotal,
} from "~/features/cart/cart/components/commonParts";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/mui";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import type { QuoteEntryRowViewModel } from "~/features/cart/activeQuote/models/QuoteTableViewModel";
import { zero } from "~/utils/PriceUtils";

interface Props {
  entryViewModel: QuoteEntryRowViewModel;
  className?: string;
  index: number;
}

const ProductEntry = (props: Props) => {
  const { entryViewModel, className } = props;
  const { product, totalQuantity, subtotal, targetSubtotal, isFcc, packageEntries, copyEntry } = entryViewModel;
  const isEditMode = packageEntries[0]?.actions !== undefined && product !== undefined;
  const { t } = useTranslation();

  const cls = classNames(className, "scha__cart__product-entry qproductentry_" + props.index);
  const showInternal = useContext(ActiveQuoteShowInternalContext);

  const base = product.base;
  const productIsLinear = isConfiguratorProduct(base);
  const packEntryCount = entryViewModel.packageEntries.length;

  const disableWS = window.SCHA.config.disableAllWS;

  return (
    <div className={cls}>
      <div className="scha__cart__product-table__left-side">
        <CartEntryProductInfo productDescriptor={product} isFcc={isFcc} />
      </div>

      <div className="scha__active-quote__product-table__right-side">
        {product.isLoading && <Spinner className={"my-2"} />}

        <PackageEntryList packageEntries={packageEntries} />

        {isEditMode && base.quantityVisible && (
          <div className="mt-3">
            <div style={{ marginLeft: "-22px" }} className={"scha__cart__product-table__right-side__summary-container"}>
              {!disableWS && !productIsLinear && (
                <div className={"pl-3"}>
                  <Button
                    size={"medium"}
                    variant={"text"}
                    data-test-id={"btn-add-row"}
                    startIcon={<DefaultButtonIcon type={IconType.Plus} />}
                    onClick={copyEntry}
                  >
                    {t("cartPage.table.copyRow")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="scha__active-quote__product-table__right-side__summary-container pt-30 p-relative">
          <QuantitySubtotal className={"pl-3 pr-2"} quantity={totalQuantity} />
          <DeliverySubtotal className={"pl-2 pr-2"} quantity={packEntryCount} isSubscription={!base.quantityVisible} />
          <PriceSubtotal className={`pl-2 pr-${!showInternal || !isEditMode ? 3 : 2}`} price={subtotal ?? zero()} />

          {showInternal && isEditMode && (
            <>
              <div className="scha__active-quote__summary-internal">
                <div className="scha__active-quote__summary-internal__bg" />
              </div>
              <div className="scha__active-quote__summary-internal">
                <div className="scha__active-quote__summary-internal__bg" />
                <TargetPriceSubtotal className={"pl-2 pr-3"} price={targetSubtotal ?? zero()} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductEntry;
